'use client';
import React from 'react';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import Section from '@repo/ui/src/components/Section';
import ExpandableContentWithMarkdown from '@repo/ui/src/components/ExpandableContentWithMarkdown';
import { FlexDirection } from '@repo/ui/src/types';
import dynamic from 'next/dynamic';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';

const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface Props {
  toutContent: Record<string, any>;
  markdownHeading: string;
  markdown: string;
  flexDisplay: FlexDirection;
  forceDivider: boolean;
  expandableTitle: string;
  disclaimerText: string;
  expandableMarkdown: string;
}

export const ToutContentWithMarkdown = ({
  toutContent,
  markdownHeading,
  markdown,
  flexDisplay,
  forceDivider,
  expandableTitle,
  disclaimerText,
  expandableMarkdown,
}: Props) => {
  const mdAndUp = useIsMD();
  const computedFlexDirection = flexDisplay || (mdAndUp ? 'row' : 'column');

  const markdownComponents = {
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="tw-list-none tw-m-0 tw-pt-2">
        {children}
      </ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="tw-pb-2 tw-relative tw-leading-[32px] tra-text-web-body-xl">
        {children}
      </li>
    ),
  };

  return (
    <Section texture gutterX gutterY basePadding>
      <div>
        <div
          className={classNames(
            'tw-flex tw-justify-between',
            computedFlexDirection === 'column' ? 'tw-flex-col' : 'tw-flex-row',
          )}
        >
          <div className="tw-pb-4 tw-max-w-[36rem] md:tw-pr-16 lg:tw-max-w-[40vw]">
            <ToutContent
              divider
              headingStyleVariant="h2"
              align="left"
              forceDivider={forceDivider}
              {...(toutContent as any)}
            />
          </div>
          <div className="tw-pt-1 tw-min-w-[30vw] xl:tw-min-w-[40vw]">
            {markdownHeading && (
              <p className="tra-text-web-heading-6 tw-font-semibold tw-mb-4">
                {markdownHeading}
              </p>
            )}
            {markdown && (
              <Markdown bodySize="xLarge" components={markdownComponents}>
                {markdown}
              </Markdown>
            )}
          </div>
        </div>
        {expandableMarkdown && (
          <div className="tw-w-full">
            <ExpandableContentWithMarkdown
              title={expandableTitle}
              markdown={expandableMarkdown}
              disclaimerText={disclaimerText}
              markdownComponents={markdownComponents}
            />
          </div>
        )}
      </div>
    </Section>
  );
};

export default ToutContentWithMarkdown;
